/**
 * OP ID identity — thin Solid wrapper around the canonical suite client
 * (`../opid-client.js`, copied verbatim per OP-ID-INTEGRATION.md — do not
 * hand-edit that file, re-copy it if the suite client changes).
 *
 * OPButtons is not a sign-in surface (DEC-166): there is no local email/code
 * form here, only the hub's suite-wide login. `SignInGate.tsx` is the only
 * consumer that redirects; every other module just reads `authIdentity()`.
 */

import { createSignal } from 'solid-js';
import { createOpId } from '../opid-client.js';
import type { OpIdIdentity, OpIdState, OpIdStateDetail } from '../opid-client.js';

export const opid = createOpId({ appId: 'opbuttons' });

export type AuthStatus = 'checking' | 'signed-in' | 'offline' | 'signed-out';
export type SignedOutReason = NonNullable<OpIdStateDetail['reason']>;

const [status, setStatus] = createSignal<AuthStatus>('checking');
const [identity, setIdentity] = createSignal<OpIdIdentity | null>(null);
const [signedOutReason, setSignedOutReason] = createSignal<SignedOutReason | null>(null);

export const authStatus = status;
export const authIdentity = identity;

/**
 * WHY this is exported (2026-09-01): the client already distinguishes "the
 * broker was unreachable" (`offline-no-cache`) from "the broker said you are
 * signed out" (`no-session`), and only the second one is a reason to send the
 * user to the hub. `SignInGate` used to re-derive that from `navigator.onLine`,
 * which is TRUE on a machine that has a LAN but no internet — i.e. exactly the
 * gig case — so it navigated to a hub it could not reach and left the operator
 * on a dead browser error page. Confirmed on the Mac: no internet, redirect
 * fired anyway.
 */
export const authSignedOutReason = signedOutReason;

async function refresh(): Promise<void> {
  const id = await opid.getIdentity();
  setIdentity(id);
  setStatus(id ? (id.offline ? 'offline' : 'signed-in') : 'signed-out');
}

opid.subscribe((state: OpIdState, detail: OpIdStateDetail) => {
  if (state === 'signed-out') {
    setIdentity(null);
    setSignedOutReason(detail?.reason ?? null);
    setStatus('signed-out');
    return;
  }
  setSignedOutReason(null);
  // 'signed-in' | 'offline' | 'switched' all mean "re-read the identity" —
  // switched in particular must land through refresh() so callers holding
  // authIdentity() see the new sub, not a stale one.
  void refresh();
});

/** Kicks off the first resolve. Call once, at app boot. */
export function initAuth(): Promise<void> {
  return refresh();
}

/** Device-wide sign-out (revoke at the broker, then clear locally). */
export function signOutOpId(): Promise<{ revoked: boolean }> {
  return opid.signOut();
}

/** Manual "Refresh identity" action (account dropdown) — re-reads the
 *  identity from a fresh token, same path `refresh()` already runs on
 *  every `opid.subscribe()` event, just callable on demand. */
export const refreshIdentity = refresh;
